import React, { useEffect, useState, useCallback } from "react";
import { Target, Plus, X, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";

const BUREAUS = [
  { key: "equifax", label: "EQUIFAX" },
  { key: "experian", label: "EXPERIAN" },
  { key: "transunion", label: "TRANSUNION" },
];

function Item({ item, editable, onToggle, onDelete, onFlag }) {
  const removed = item.status === "removed";
  const isNew = !!item.new_item;
  return (
    <div
      className={`group flex items-start justify-between gap-2 rounded-lg px-3 py-2 border text-sm transition-all ${
        removed
          ? "bg-green-500/10 border-green-600/30"
          : isNew
          ? "bg-amber-500/10 border-amber-500/50"
          : "bg-[#F5F4EF] border-[#23261C]/10"
      } ${editable ? "cursor-pointer hover:border-[#4F5D1E]/50" : ""}`}
      onClick={editable ? () => onToggle(item) : undefined}
      title={editable ? (removed ? "Click to mark as still working" : "Click to mark as REMOVED") : undefined}
      data-testid={`tracker-item-${item.id}`}
    >
      <span
        className={`leading-snug ${removed ? "line-through text-green-700" : "text-[#23261C]/85"}`}
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {item.name}
      </span>
      <span className="flex items-center gap-1.5 shrink-0 mt-0.5">
        {isNew && !removed && (
          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-600" data-testid={`tracker-new-badge-${item.id}`}>
            <AlertTriangle className="w-3.5 h-3.5" /> New — added during repair
          </span>
        )}
        {removed && (
          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-green-700" data-testid={`tracker-removed-badge-${item.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Removed
          </span>
        )}
        {editable && !removed && (
          <button
            onClick={(e) => { e.stopPropagation(); onFlag(item); }}
            className={`transition-all ${isNew ? "text-amber-500 hover:text-amber-700" : "opacity-100 md:opacity-0 md:group-hover:opacity-100 text-[#23261C]/30 hover:text-amber-600"}`}
            title={isNew ? "Unmark: no longer flagged as newly added" : "Flag as a NEW item that appeared during repair"}
            data-testid={`tracker-flag-${item.id}`}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
          </button>
        )}
        {editable && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(item); }}
            className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity"
            title="Delete item"
            data-testid={`tracker-delete-${item.id}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}

export default function BureauTracker({ userId = null }) {
  const editable = !!userId; // admin mode
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({ equifax: "", experian: "", transunion: "" });

  const load = useCallback(async () => {
    try {
      const url = editable ? `/admin/clients/${userId}/tracker` : "/tracker/me";
      const { data } = await api.get(url);
      setData(data);
    } catch { setData({ equifax: [], experian: [], transunion: [] }); }
  }, [editable, userId]);

  useEffect(() => { load(); }, [load]);

  const add = async (bureau) => {
    const name = (drafts[bureau] || "").trim();
    if (name.length < 2) return;
    try {
      await api.post(`/admin/clients/${userId}/tracker`, { bureau, name });
      setDrafts({ ...drafts, [bureau]: "" });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't add item."); }
  };

  const toggle = async (item) => {
    try {
      await api.patch(`/admin/tracker/${item.id}`, { status: item.status === "removed" ? "working" : "removed" });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't update item."); }
  };

  const flag = async (item) => {
    try {
      await api.patch(`/admin/tracker/${item.id}`, { new_item: !item.new_item });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't update item."); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from the tracker?`)) return;
    try { await api.delete(`/admin/tracker/${item.id}`); await load(); }
    catch (e) { alert(e?.response?.data?.detail || "Couldn't delete item."); }
  };

  if (!data) return null;
  const total = BUREAUS.reduce((n, b) => n + (data[b.key] || []).length, 0);
  if (!editable && total === 0) return null; // hide empty tracker from clients

  const removedCount = BUREAUS.reduce((n, b) => n + (data[b.key] || []).filter((i) => i.status === "removed").length, 0);
  const newCount = BUREAUS.reduce((n, b) => n + (data[b.key] || []).filter((i) => i.new_item && i.status !== "removed").length, 0);

  return (
    <div className="card-dark p-5 mb-5" data-testid="bureau-tracker">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-[var(--if-gold)]" />
          <span className="font-mono-label text-[var(--if-gold)]">Dispute Battle Plan</span>
        </div>
        {total > 0 && (
          <span className="flex items-center gap-3 text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
            {newCount > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-600 font-semibold" data-testid="tracker-new-count">
                <AlertTriangle className="w-3.5 h-3.5" /> {newCount} new item{newCount > 1 ? "s" : ""} appeared during repair
              </span>
            )}
            <span data-testid="tracker-progress">
              <strong className="text-green-700">{removedCount}</strong> of {total} negatives removed
            </span>
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {BUREAUS.map((b) => (
          <div key={b.key} className="rounded-xl border border-[#23261C]/12 bg-white p-4" data-testid={`tracker-box-${b.key}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-bebas text-lg text-[#23261C]" style={{ letterSpacing: "0.08em" }}>{b.label}</span>
              <span className="text-[10px] text-white/45" style={{ fontFamily: "Inter, sans-serif" }}>
                {(data[b.key] || []).filter((i) => i.status === "removed").length}/{(data[b.key] || []).length} removed
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {(data[b.key] || []).map((item) => (
                <Item key={item.id} item={item} editable={editable} onToggle={toggle} onDelete={remove} onFlag={flag} />
              ))}
              {(data[b.key] || []).length === 0 && (
                <p className="text-xs text-white/40 italic" style={{ fontFamily: "Inter, sans-serif" }}>
                  {editable ? "No items yet — add the negatives you're disputing." : "Nothing being worked for this bureau."}
                </p>
              )}
            </div>
            {editable && (
              <div className="flex gap-1.5 mt-3">
                <input
                  className="if-input !py-2 !text-xs flex-1"
                  placeholder="e.g. Midland Credit — collection"
                  value={drafts[b.key]}
                  onChange={(e) => setDrafts({ ...drafts, [b.key]: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && add(b.key)}
                  data-testid={`tracker-input-${b.key}`}
                />
                <button onClick={() => add(b.key)} className="btn-gold !py-2 !px-3 !text-[11px] shrink-0" data-testid={`tracker-add-${b.key}`}>
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      {editable && (
        <p className="text-[10px] text-white/40 mt-3" style={{ fontFamily: "Inter, sans-serif" }}>
          Click an item to toggle REMOVED. Use the ⚠ icon to flag negatives that appeared during repair — clients see both live on their dashboard.
        </p>
      )}
    </div>
  );
}
